import React from 'react';

import {Operator} from 'search/SearchConstants';
import {Size} from 'ui-library/inputs/InputConstants';
import TextInput from 'ui-library/inputs/TextInput';
import * as Keyboard from 'util/Keyboard';
import pickDefined from 'util/pickDefined';
import {ElementClipboardEvent, InputChangeEvent} from 'util/Types';

import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './FilterFormConstants';
import FilterFormSkeletonNodeLayout from './FilterFormSkeletonNodeLayout';
import OperatorDropdown from './OperatorDropdown';
import PrimaryFilterFormSkeleton from './PrimaryFilterFormSkeleton';
import SecondaryFilterFormSkeleton from './SecondaryFilterFormSkeleton';
import withDebouncing from './withDebouncing';

interface TextValueFilterFormProps extends BaseFilterFormProps {
  filterStyle: FilterStyle;
  onChangeOperator: (value: Operator) => void;
  onChangeValue: (value: string) => void;
  onEnter?: () => void;
  onPaste?: (e: ElementClipboardEvent) => void;
  onRemove?: () => void;
  operators: Array<Operator>;
  selectedOperator: Operator;
  value: string;
}

const TextValueFilterForm: React.SFC<TextValueFilterFormProps> = (
  props: TextValueFilterFormProps
): JSX.Element => {
  const skeletonProps = pickDefined(props, ...BASE_FILTER_FORM_PROP_NAMES);
  const operatorDropdown = (
    <OperatorDropdown
      buttonSize={props.buttonSize}
      onChangeOperator={props.onChangeOperator}
      operators={props.operators}
      selectedOperator={props.selectedOperator}
    />
  );
  const textInput = (
    <TextInput
      onChange={(e: InputChangeEvent) => {
        props.onChangeValue(e.target.value);
      }}
      onKeyDown={Keyboard.callOnEnter(props.onEnter)}
      onPaste={props.onPaste}
      placeholder="value"
      size={Size.SMALL}
      value={props.value}
      dataTestAttrs={{
        component: 'TextValueFilterForm',
        element: 'textInput',
      }}
    />
  );
  if (props.filterStyle === FilterStyle.PRIMARY) {
    return (
      <PrimaryFilterFormSkeleton
        {...skeletonProps}
        content={<FilterFormSkeletonNodeLayout nodeGrid={[[operatorDropdown], [textInput]]} />}
        dropdownToggleText={props.dropdownToggleText}
      />
    );
  } else {
    if (!props.onRemove) {
      throw new Error('Expected props.onRemove to be present when filterStyle is SECONDARY');
    }
    return (
      <SecondaryFilterFormSkeleton
        {...skeletonProps}
        nodeGrid={[[operatorDropdown], [textInput]]}
        onChangeFilterKey={props.onChangeFilterKey}
        selectedFilterKey={props.selectedFilterKey}
        onRemove={props.onRemove}
      />
    );
  }
};

export default withDebouncing(TextValueFilterForm);
